import { SpyneTrait } from 'spyne';
import { UISearchView } from 'components/page-items/ui-search-view.js';

/**
 * Logic for CustomersTableView.
 *
 * ── Rows are markup, not views ──────────────────────────────────────────────
 *
 * A customer row displays and nothing else — no click, no action, no state of
 * its own. So the rows are generated by the template's `{{#customers}}` section
 * rather than by a ViewStream per row. Minting a child view is for an element
 * that needs a channel its parent does not listen to; a row that only shows
 * text needs none. [mint-child-view-by-channel-need]
 *
 * The template stays logic-less: every decision — the image path, the alt text —
 * is resolved here, and the section iterates a finished array.
 * [shape-data-for-logicless-template]
 *
 * The invoices table is deliberately not this. Each of its rows carries update
 * and delete controls, so those rows are ViewStreams.
 */
export class CustomersTableTraits extends SpyneTrait {
  constructor(context) {
    let traitPrefix = 'customersTable$';
    super(context, traitPrefix);
  }

  /**
   * Search sits inside the table in the source, so it is nested here rather than
   * added as a sibling pageItem — it belongs to this region.
   *
   * Its own eventType is `acmeSearch`, so it filters the customers already in
   * hand rather than returning to the server.
   */
  static customersTable$OnRendered(props = this.props) {
    this.appendView(
      new UISearchView({
        data: {
          inputId: 'customers-search',
          placeholder: props.data.searchPlaceholder,
          btnType: 'filter-customers',
        },
      }),
      `[data-slot='search']`,
    );

    props.customerEls$ = props.el$('[data-customer-id]').els;
    this.customersTable$ApplyVisibility();
  }

  /**
   * The relayed page slice — the pagination ViewStream's answer to the
   * current URL's query AND page. Every row outside it hides; the totals a
   * visible row shows are untouched, because they arrive computed over ALL of
   * that customer's invoices in the dump, not over what is on screen.
   */
  static customersTable$OnVisibleIds(e, props = this.props) {
    props.customerVisiblePayload = e?.payload || null;
    this.customersTable$ApplyVisibility();
  }

  static customersTable$ApplyVisibility(props = this.props) {
    if (!props.customerEls$ || !props.customerVisiblePayload) return;

    const visibleIds = new Set(props.customerVisiblePayload.visibleIds || []);

    props.customerEls$.forEach((el) => {
      el.classList.toggle(
        'hidden',
        visibleIds.has(el.dataset.customerId) === false,
      );
    });
  }

  /**
   * Shapes the dump's customers into what the template's section renders.
   *
   * `total_invoices`, `total_pending` and `total_paid` arrive display-ready —
   * fetchFilteredCustomers formats the currency in SQL, matching the Next.js
   * query — so the only work is the image path and the alt text.
   *
   * The seed data stores `/customers/<name>.png`, which the Next.js app resolves
   * against public/. This app has no public root, so the path is prefixed with
   * `imgs` — the convention that resolves against IMG_PATH, landing on
   * /static/imgs in dev and /assets/static/imgs in a production build.
   */
  static customersTable$GetRows(customers = []) {
    return customers.map((customer) => ({
      attrCustomerId: customer.id,
      name: customer.name,
      email: customer.email,
      totalInvoices: customer.total_invoices,
      totalPending: customer.total_pending,
      totalPaid: customer.total_paid,
      attrImageSrc: 'imgs' + customer.image_url,
      attrImageAlt: `${customer.name}'s profile picture`,
    }));
  }
}
